import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { getAdminData, postInstituteAppData, deleteInstituteAppData, apiPaths } from 'modules/platform-components/api-integration-util';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';

const permissionCategories = [
  {
    name: 'Dashboard',
    subCategories: [
      { name: 'Add Question', subPermissions: ['Access'] },
      { name: 'Upload Question', subPermissions: ['Access'] },
      { name: 'Sent For Approval', subPermissions: ['Access'] },
      { name: 'Approve', subPermissions: ['Access'] },
      { name: 'Correction Needed', subPermissions: ['Access'] },
      { name: 'Reject', subPermissions: ['Access'] },
      { name: 'Delete', subPermissions: ['Access'] },
    ]
  },
  {
    name: 'Question Repo',
    subCategories: [
      { name: 'Branch', subPermissions: ['Access'] },
      { name: 'Batch', subPermissions: ['Access'] },
      { name: 'Batch Description', subPermissions: ['Access'] },
    ]
  },
  {
    name: 'Exams',
    subCategories: [
      { name: 'Create Exam', subPermissions: ['Access'] },
      { name: 'Copy Exam', subPermissions: ['Access'] },
       { name: 'Sent For Approval', subPermissions: ['Access'] },
      { name: 'Approve', subPermissions: ['Access'] },
      { name: 'Rejected', subPermissions: ['Access'] },
      { name: 'Delete', subPermissions: ['Access'] },
      { name: 'Push To App', subPermissions: ['Access'] },
      { name: 'Download', subPermissions: ['Access'] },
    ]
  },
  {
    name: 'Students Feedback',
    subCategories: [
      { name: 'Show Completed Feedback', subPermissions: ['Access'] },
    ]
  },
  {
    name: 'Test',
    subCategories: [
      { name: 'Normal Tests', subPermissions: ['Access'] },
      { name: 'Custom Tests', subPermissions: ['Access'] },
    ]
  },
  {
    name: 'User Management',
    subCategories: [
      { name: 'Users', subPermissions: ['Access'] },
      { name: 'Roles', subPermissions: ['Access'] },
    ]
  },
];

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch roles
        const rolesResponse = await getAdminData(apiPaths.acadRoles);
        if (rolesResponse.result && Array.isArray(rolesResponse.result)) {
          const formattedRoles = rolesResponse.result.map(role => ({
            ...role,
            permissions: role.rolePermissions ? JSON.parse(role.rolePermissions) : {}
          }));
          setRoles(formattedRoles);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        showSnackbar('Failed to fetch data', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenAddModal = () => {
    setEditMode(false);
    setCurrentRoleId(null);
    setRoleName('');
    setSelectedPermissions({});
    setOpenModal(true);
  };

  const handleOpenEditModal = (role) => {
    setEditMode(true);
    setCurrentRoleId(role.roleId);
    setRoleName(role.roleName);
    setSelectedPermissions(JSON.parse(JSON.stringify(role.permissions)));
    setOpenModal(true);
  };

  const handleCheckboxChange = (category, subCategory = null, subPermission = null) => {
    setSelectedPermissions((prev) => {
      const updated = { ...prev };

      if (subCategory && subPermission) {
        if (!updated[category]) updated[category] = {};
        if (!updated[category][subCategory]) updated[category][subCategory] = [];

        if (updated[category][subCategory].includes(subPermission)) {
          updated[category][subCategory] = updated[category][subCategory].filter(p => p !== subPermission);
        } else {
          updated[category][subCategory].push(subPermission);
        }

        if (updated[category][subCategory].length === 0) {
          delete updated[category][subCategory];
        }
      } else if (subCategory) {
        if (!updated[category]) updated[category] = {};

        const allSubPermissions = permissionCategories
          .find(p => p.name === category)
          ?.subCategories.find(s => s.name === subCategory)
          ?.subPermissions || [];

        if (updated[category][subCategory]) {
          delete updated[category][subCategory];
        } else {
          updated[category][subCategory] = [...allSubPermissions];
        }
      } else {
        const allSubCategories = permissionCategories.find(p => p.name === category)?.subCategories || [];

        if (updated[category]) {
          delete updated[category];
        } else {
          updated[category] = {};
          allSubCategories.forEach(subCat => {
            updated[category][subCat.name] = [...subCat.subPermissions];
          });
        }
      }

      if (updated[category] && Object.keys(updated[category]).length === 0) {
        delete updated[category];
      }

      return updated;
    });
  };

  const handleSaveRole = async () => {
    if (!roleName.trim()) {
      showSnackbar('Role name is required', 'error');
      return;
    }

    try {
      const roleData = {
        roleName: roleName.trim(),
        rolePermissions: JSON.stringify(selectedPermissions),
        appCode: 'ACAD_PORTAL'
      };

      let response;
      if (editMode) {
        response = await postInstituteAppData(apiPaths.acadSaveRole, {
          data: { ...roleData, roleId: currentRoleId },
        });

        // Update the existing role in state
        setRoles(prevRoles => prevRoles.map(role =>
          role.roleId === currentRoleId ? {
            ...role,
            roleName: roleName.trim(),
            permissions: selectedPermissions
          } : role
        ));
      } else {
        response = await postInstituteAppData(apiPaths.acadSaveRole, {
          data: roleData,
        });

        // Add new role to state
        if (response.result) {
          setRoles(prevRoles => [...prevRoles, {
            roleId: response.result.roleId,
            roleName: roleName.trim(),
            permissions: selectedPermissions,
            rolePermissions: JSON.stringify(selectedPermissions)
          }]);
        }
      }

      setOpenModal(false);
      showSnackbar(`Role ${editMode ? 'updated' : 'created'} successfully`);
    } catch (error) {
      console.error('Error saving role:', error);
      showSnackbar(`Failed to ${editMode ? 'update' : 'create'} role: ${error.message}`, 'error');
    }
  };

  const deleteRole = async (roleId) => {
    try {
      const userDetails = getUserDetails();

      const response = await deleteInstituteAppData(apiPaths.deleteRole, {
        body: [roleId],
        headers: {
          tenantId: userDetails.tenantId
        },
        params: {
          userEmail: userDetails.email
        }
      });

      if (response && (response.status === 200 || response.responseCode === 0)) {
        setRoles(prevRoles => prevRoles.filter(role => role.roleId !== roleId));
        showSnackbar(response.msg || 'Role deleted successfully');
        return true;
      }

      throw new Error(response?.msg || 'Failed to delete role');
    } catch (error) {
      console.error('Error deleting role:', error);
      showSnackbar(error.message || 'Failed to delete role', 'error');
      return false;
    }
  };


  const isCategoryChecked = (categoryName) => {
    const category = permissionCategories.find(c => c.name === categoryName);
    if (!category) return false;

    return category.subCategories.every(subCat =>
      selectedPermissions[categoryName]?.[subCat.name]?.length === subCat.subPermissions.length
    );
  };

  const isSubCategoryChecked = (categoryName, subCategoryName) => {
    const subCategory = permissionCategories
      .find(c => c.name === categoryName)
      ?.subCategories.find(s => s.name === subCategoryName);

    if (!subCategory) return false;

    return selectedPermissions[categoryName]?.[subCategoryName]?.length === subCategory.subPermissions.length;
  };

  const formatPermissionsForDisplay = (permissions) => {
    const result = [];

    for (const [category, subCategories] of Object.entries(permissions)) {
      const categoryObj = permissionCategories.find(c => c.name === category);
      if (!categoryObj) continue;

      const subItems = [];

      for (const [subCategory, perms] of Object.entries(subCategories)) {
        const subCategoryObj = categoryObj.subCategories.find(s => s.name === subCategory);
        if (!subCategoryObj) continue;

        if (subCategory === 'Access' && categoryObj.subCategories.length === 1 && categoryObj.subCategories[0].name === 'Access') {
          subItems.push(category);
        } else {
          subItems.push(subCategory);
        }
      }

      if (subItems.length > 0) {
        result.push({
          category,
          subItems
        });
      }
    }

    return result;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Role Management
        </Typography>

        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddModal}
            sx={{
              color: '#3e4095',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: '#3e4095', color: 'white' },
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '4px'
            }}
          >
            Add New Role
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {roles.map(role => {
            const formattedPermissions = formatPermissionsForDisplay(role.permissions);

            return (
              <Card key={role.roleId} sx={{ width: '100%', maxWidth: 400, p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">{role.roleName}</Typography>
                  <Box>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEditModal(role)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => deleteRole(role.roleId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Permissions:</Typography>
                  {formattedPermissions.map((item, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{item.category}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, ml: 2 }}>
                        {item.subItems.map((subItem, subIndex) => (
                          <Chip
                            key={subIndex}
                            label={subItem}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* Role Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Role' : 'Create New Role'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Role Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" sx={{ mb: 2 }}>Page Permissions</Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>Configure which pages this role can access:</Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              size="small"
              sx={{ mr: 1 }}
              onClick={() => {
                const allPermissions = {};
                permissionCategories.forEach(category => {
                  allPermissions[category.name] = {};
                  category.subCategories.forEach(subCat => {
                    allPermissions[category.name][subCat.name] = [...subCat.subPermissions];
                  });
                });
                setSelectedPermissions(allPermissions);
              }}
            >
              SELECT ALL
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedPermissions({})}
            >
              SELECT NONE
            </Button>
          </Box>

          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {permissionCategories.map(category => (
              <Box key={category.name} sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCategoryChecked(category.name)}
                      indeterminate={
                        selectedPermissions[category.name] &&
                        !isCategoryChecked(category.name) &&
                        Object.keys(selectedPermissions[category.name]).length > 0
                      }
                      onChange={() => handleCheckboxChange(category.name)}
                    />
                  }
                  label={<Typography fontWeight="bold">{category.name}</Typography>}
                />

                <Box sx={{ ml: 4 }}>
                  {category.subCategories.map(subCategory => (
                    <Box key={subCategory.name} sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSubCategoryChecked(category.name, subCategory.name)}
                            indeterminate={
                              selectedPermissions[category.name]?.[subCategory.name] &&
                              !isSubCategoryChecked(category.name, subCategory.name) &&
                              selectedPermissions[category.name][subCategory.name].length > 0
                            }
                            onChange={() => handleCheckboxChange(category.name, subCategory.name)}
                          />
                        }
                        label={<Typography variant="body1">{subCategory.name}</Typography>}
                      />

                      <Box sx={{ ml: 4 }}>
                        {subCategory.subPermissions.map(permission => (
                          <FormControlLabel
                            key={permission}
                            control={
                              <Checkbox
                                checked={selectedPermissions[category.name]?.[subCategory.name]?.includes(permission) || false}
                                onChange={() => handleCheckboxChange(category.name, subCategory.name, permission)}
                              />
                            }
                            label={<Typography variant="body2">{permission}</Typography>}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRole}
            variant="contained"
            disabled={!roleName.trim()}
            sx={{ minWidth: 120 }}
          >
            {editMode ? 'Update Role' : 'Save Role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Roles;